import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Label,
  Message,
  Modal,
  Progress,
  Segment,
  Step,
  Table,
} from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';
import EditableNumberInput from './importWizard/EditableNumberInput';
import ImportPreviewTable from './importWizard/ImportPreviewTable';
import ImportWizardModal from './importWizard/ImportWizardModal';

const parseStockInput = (text) => {
  const normalized = String(text || '').replace(/[\s.]/g, '');
  if (!/^\d+$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isSafeInteger(value) && value <= 2147483647 ? value : null;
};

const STEP_TEMPLATE = 0;
const STEP_VALIDATION = 1;
const STEP_EXECUTION = 2;
const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
const MAX_IMPORT_ROWS = 10000;

const STOCK_STATUS_OPTIONS = [
  { key: 'all', value: 'all', text: 'Todos' },
  { key: 'with_stock', value: 'with_stock', text: 'Con stock' },
  { key: 'without_stock', value: 'without_stock', text: 'Sin stock' },
];

const MARKETPLACE_OPTIONS = [
  { key: 'all', value: '', text: 'Todos los canales' },
  { key: 'mercado_libre', value: 'mercado_libre', text: 'Mercado Libre' },
  { key: 'tienda_nube', value: 'tienda_nube', text: 'Tienda Nube' },
];

const MARKETPLACE_LABELS = {
  mercado_libre: 'Mercado Libre',
  tienda_nube: 'Tienda Nube',
};

const parseSkuFilters = (value) => {
  const uniqueSkus = new Map();
  String(value || '')
    .split(/[\s,]+/)
    .map((sku) => sku.trim())
    .filter(Boolean)
    .forEach((sku) => {
      const key = sku.toLocaleLowerCase();
      if (!uniqueSkus.has(key)) uniqueSkus.set(key, sku);
    });
  return Array.from(uniqueSkus.values());
};

const getSellerMarketplace = (seller) =>
  String(seller?.accountType || seller?.account_type || '').trim().toLowerCase();

const countCsvDataRows = (content) => {
  const physicalRows = String(content || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);
  return Math.max(0, physicalRows.length - 1);
};

const formatStock = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  return String(Math.max(0, Math.floor(number)));
};

const formatDelta = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number === 0) return '0';
  return number > 0 ? `+${number}` : String(number);
};

const resolveRowStatusColor = (status, rowErrors = []) => {
  if (status === 'VALID') return 'green';
  if (status === 'SKIPPED' && rowErrors.length > 0) return 'orange';
  if (status === 'ERROR') return 'red';
  return 'grey';
};

const resolveRowStatusText = (status, rowErrors = []) => {
  if (status === 'VALID') return 'Se actualiza';
  if (status === 'SKIPPED' && rowErrors.length > 0) return 'Omitida';
  if (status === 'SKIPPED') return 'Sin cambios';
  if (status === 'ERROR') return 'Error';
  return status || '-';
};

const resolveDeltaColor = (value) => {
  const number = Number(value || 0);
  if (number > 0) return '#16783a';
  if (number < 0) return '#b42318';
  return '#667085';
};

const resolveValidationRowStyle = (row) => {
  if (row.status === 'VALID') return { background: '#fbfffc' };
  if (row.errors?.length > 0) return { background: '#fffaf3' };
  return {};
};

const orderPreviewRows = (rows) =>
  [...(rows || [])].sort((left, right) => {
    const leftWillChange = left.status === 'VALID' && left.hasStockChange === true;
    const rightWillChange = right.status === 'VALID' && right.hasStockChange === true;
    if (leftWillChange !== rightWillChange) {
      return leftWillChange ? -1 : 1;
    }
    return Number(left.rowNumber || 0) - Number(right.rowNumber || 0);
  });

const resolveImpactLabel = (row) => {
  if (row.executionPath === 'ITEM_ADJUSTMENT') return 'Solo MercuryCom';
  const count = Array.isArray(row.impactedListings) ? row.impactedListings.length : 0;
  if (count === 0) return 'Sin publicaciones';
  if (count === 1) return '1 publicacion';
  return `${count} publicaciones`;
};

const buildPendingImportState = (importId, previewSummary) => ({
  importId,
  status: 'RUNNING',
  totalRows: Number(previewSummary?.validRows || 0),
  processedRows: 0,
  stockUpdatedItems: 0,
  fanOutUpdatedTargets: 0,
  fanOutFailedTargets: 0,
  failedRows: 0,
  results: [],
});

const BulkStockImportWizardModal = ({
  open,
  onClose,
  authUser,
  onCompleted,
}) => {
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importState, setImportState] = useState(null);
  const [activeStep, setActiveStep] = useState(STEP_TEMPLATE);
  const [error, setError] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [skuFilterInput, setSkuFilterInput] = useState('');
  const [stockStatus, setStockStatus] = useState('all');
  const [marketplace, setMarketplace] = useState('');
  const [sellerIds, setSellerIds] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [templateSkuCount, setTemplateSkuCount] = useState(null);
  const [templateSummaryLoading, setTemplateSummaryLoading] = useState(false);
  const [templateSummaryError, setTemplateSummaryError] = useState('');

  const previewSummary = previewData?.summary || null;
  const importProgress = importState || null;
  const importRunning = executionLoading || importProgress?.status === 'RUNNING';
  const orderedPreviewRows = useMemo(
    () => orderPreviewRows(previewData?.rows),
    [previewData?.rows],
  );
  const templateFilters = useMemo(
    () => ({
      skus: parseSkuFilters(skuFilterInput),
      stockStatus,
      ...(marketplace ? { marketplace } : {}),
      ...(marketplace && sellerIds.length > 0 ? { sellerIds } : {}),
    }),
    [skuFilterInput, stockStatus, marketplace, sellerIds],
  );
  const filteredSellers = useMemo(
    () => sellers.filter((seller) => getSellerMarketplace(seller) === marketplace),
    [sellers, marketplace],
  );
  const sellerOptions = useMemo(
    () => filteredSellers.map((seller) => ({
      key: String(seller.id),
      value: String(seller.id),
      text: seller.nickname || seller.email || `Cuenta ${seller.id}`,
    })),
    [filteredSellers],
  );
  const templateScopeLabel = useMemo(() => {
    const parts = [];
    const selectedSkus = templateFilters.skus.length;
    parts.push(selectedSkus > 0 ? `${selectedSkus} SKU indicados` : 'Todos los SKU');
    parts.push(STOCK_STATUS_OPTIONS.find((option) => option.value === stockStatus)?.text || 'Todos');
    if (marketplace) {
      parts.push(MARKETPLACE_LABELS[marketplace] || marketplace);
      if (sellerIds.length > 0) {
        parts.push(`${sellerIds.length} ${sellerIds.length === 1 ? 'cuenta' : 'cuentas'}`);
      }
    } else {
      parts.push('Todos los canales');
    }
    return parts.join(' · ');
  }, [templateFilters.skus.length, stockStatus, marketplace, sellerIds.length]);

  const progressPercent = useMemo(() => {
    const total = Number(importProgress?.totalRows || 0);
    const processed = Number(importProgress?.processedRows || 0);
    if (!total) return 0;
    return Math.min(100, Math.round((processed / total) * 100));
  }, [importProgress]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFileName('');
    setFileContent('');
    setPreviewData(null);
    setImportState(null);
    setActiveStep(STEP_TEMPLATE);
    setError('');
    setTemplateMessage('');
    setSkuFilterInput('');
    setStockStatus('all');
    setMarketplace('');
    setSellerIds([]);
    setTemplateSkuCount(null);
    setTemplateSummaryError('');
  }, [open]);

  useEffect(() => {
    if (!open || !authUser) return undefined;
    let cancelled = false;
    stockyApi.getSellers(authUser)
      .then((response) => {
        if (!cancelled) setSellers(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setSellers([]);
      });
    return () => { cancelled = true; };
  }, [open, authUser]);

  useEffect(() => {
    if (!open || !authUser) return undefined;
    let cancelled = false;
    setTemplateSummaryLoading(true);
    setTemplateSummaryError('');
    const timeout = setTimeout(() => {
      stockyApi.getStockImportTemplateSummary(templateFilters, authUser)
        .then((response) => {
          if (!cancelled) setTemplateSkuCount(Number(response?.data?.skuCount || 0));
        })
        .catch(() => {
          if (!cancelled) {
            setTemplateSkuCount(null);
            setTemplateSummaryError('No se pudo calcular la cantidad de SKU de la descarga.');
          }
        })
        .finally(() => {
          if (!cancelled) setTemplateSummaryLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [open, authUser, templateFilters]);

  useEffect(() => {
    if (!open || !importRunning) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, importRunning]);

  useEffect(() => {
    if (!open || !authUser || !importProgress?.importId) {
      return undefined;
    }
    if (importProgress.status === 'COMPLETED' || importProgress.status === 'FAILED') {
      return undefined;
    }

    let cancelled = false;
    const syncStatus = () => {
      stockyApi
        .getStockImportStatus(importProgress.importId, authUser)
        .then((response) => {
          if (cancelled) return;
          const nextState = response?.data || null;
          setImportState(nextState);
          setExecutionLoading(false);
          if (nextState?.status === 'COMPLETED' && typeof onCompleted === 'function') {
            onCompleted(nextState);
          }
        })
        .catch((requestError) => {
          if (cancelled) return;
          setExecutionLoading(false);
          setError(requestError?.response?.data?.message || 'No se pudo obtener el progreso de la importacion.');
        });
    };

    syncStatus();
    const interval = setInterval(syncStatus, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, authUser, importProgress?.importId, importProgress?.status, onCompleted]);

  const handleDownloadTemplate = async () => {
    if (!authUser) return;

    setError('');
    setTemplateMessage('');
    setDownloadLoading(true);

    try {
      const response = await stockyApi.downloadStockImportTemplate(templateFilters, authUser);
      const dispositionHeader = response?.headers?.['content-disposition'] || '';
      const fileNameMatch = /filename="?([^";]+)"?/i.exec(dispositionHeader);
      const downloadName = fileNameMatch?.[1] || 'stock-template.csv';
      const blob = new Blob([response.data], {
        type: response?.headers?.['content-type'] || 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const rowCount = Number(response?.headers?.['x-export-row-count']);
      const countLabel = Number.isFinite(rowCount)
        ? ` Incluye ${rowCount} SKU.`
        : '';
      setTemplateMessage(`Plantilla descargada: ${downloadName}.${countLabel}`);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo descargar la plantilla.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const rejectFile = (message) => {
      event.target.value = '';
      setFileName('');
      setFileContent('');
      setPreviewData(null);
      setImportState(null);
      setError(message);
    };

    const fileNameValue = String(file.name || '').trim();
    if (!fileNameValue.toLowerCase().endsWith('.csv')) {
      rejectFile('Carga un archivo CSV solamente. No se aceptan XLSX ni otros formatos.');
      return;
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      rejectFile('El archivo CSV no puede superar los 5 MB.');
      return;
    }

    try {
      const text = await file.text();
      if (countCsvDataRows(text) > MAX_IMPORT_ROWS) {
        rejectFile('El archivo CSV no puede superar las 10000 filas.');
        return;
      }
      setFileName(file.name);
      setFileContent(text);
      setPreviewData(null);
      setImportState(null);
      setError('');
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : 'No se pudo leer el archivo.');
    }
  };

  const handlePreview = async () => {
    if (!fileContent || !authUser) return;

    setPreviewLoading(true);
    setError('');

    try {
      const response = await stockyApi.previewStockImport({ fileName, fileContent }, authUser);
      setPreviewData(response?.data || null);
      setActiveStep(STEP_VALIDATION);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo previsualizar la importacion.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!previewData?.draftId || !authUser) return;

    setExecutionLoading(true);
    setError('');
    setActiveStep(STEP_EXECUTION);

    try {
      const response = await stockyApi.executeStockImport({ draftId: previewData.draftId }, authUser);
      const importId = response?.data?.importId;
      setImportState(buildPendingImportState(importId, previewSummary));
    } catch (requestError) {
      setExecutionLoading(false);
      setError(requestError?.response?.data?.message || 'No se pudo ejecutar la importacion.');
    }
  };

  const previewHasBlockingErrors = Number(previewSummary?.errorRows || 0) > 0;
  const previewHasValidRows = Number(previewSummary?.validRows || 0) > 0;
  const executeDisabled =
    !previewData || !previewHasValidRows || previewHasBlockingErrors || executionLoading;

  const renderStepHeader = () => (
    <Step.Group ordered fluid stackable="tablet" size="mini" style={{ marginBottom: 16 }}>
      <Step active={activeStep === STEP_TEMPLATE} completed={activeStep > STEP_TEMPLATE}>
        <Step.Content>
          <Step.Title>Plantilla</Step.Title>
          <Step.Description>Descarga opcional y carga del CSV</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_VALIDATION} completed={activeStep > STEP_VALIDATION}>
        <Step.Content>
          <Step.Title>Validacion</Step.Title>
          <Step.Description>Revisa los cambios detectados</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_EXECUTION}>
        <Step.Content>
          <Step.Title>Ejecucion</Step.Title>
          <Step.Description>Seguimiento y resultado final</Step.Description>
        </Step.Content>
      </Step>
    </Step.Group>
  );

  const renderTemplateStep = () => (
    <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ border: '1px solid #dcdcdc', borderRadius: 12, padding: 20, background: '#fff', display: 'grid', gap: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Descarga la planilla o sube tu CSV</div>
        <Message
          info
          style={{ margin: 0 }}
          content="Descarga la plantilla y modifica unicamente la columna stock_nuevo. Las columnas item_id, SKU y el resto de los datos se usan para validar cada fila. Solo se aplican filas validas donde stock_nuevo sea distinto al stock actual; las demas se omiten."
        />
        <Message
          warning
          style={{ margin: 0 }}
          content="Carga archivos CSV solamente, de hasta 5 MB y 10000 filas. No se aceptan archivos XLSX en este importador."
        />
        <div style={{ display: 'grid', gap: 12, padding: 16, border: '1px solid #dce8f8', borderRadius: 10, background: '#f8fbff' }}>
          <div style={{ fontWeight: 700 }}>Alcance de la plantilla</div>
          <div style={{ color: '#52606d', fontSize: 13 }}>
            Buscá o pegá SKU separados por coma, espacios o saltos de línea. Si lo dejás vacío, se descargan todos los SKU que coincidan con los demás filtros.
          </div>
          <textarea
            aria-label="SKU para la plantilla"
            rows={3}
            value={skuFilterInput}
            onChange={(event) => setSkuFilterInput(event.target.value)}
            placeholder="SKU-001, SKU-002 o un SKU por línea"
            style={{ width: '100%', resize: 'vertical', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
            <div>
              <label htmlFor="stock-template-stock-status" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Estado de stock</label>
              <Dropdown
                id="stock-template-stock-status"
                fluid
                selection
                options={STOCK_STATUS_OPTIONS}
                value={stockStatus}
                onChange={(_, data) => setStockStatus(String(data.value || 'all'))}
              />
            </div>
            <div>
              <label htmlFor="stock-template-marketplace" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Canal</label>
              <Dropdown
                id="stock-template-marketplace"
                fluid
                selection
                options={MARKETPLACE_OPTIONS}
                value={marketplace}
                onChange={(_, data) => {
                  setMarketplace(String(data.value || ''));
                  setSellerIds([]);
                }}
              />
            </div>
            {marketplace ? (
              <div>
                <label htmlFor="stock-template-sellers" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Cuentas</label>
                <Dropdown
                  id="stock-template-sellers"
                  fluid
                  multiple
                  search
                  selection
                  clearable
                  placeholder="Todas las cuentas"
                  options={sellerOptions}
                  value={sellerIds}
                  onChange={(_, data) => setSellerIds(Array.isArray(data.value) ? data.value.map(String) : [])}
                />
              </div>
            ) : null}
          </div>
          <Message info style={{ margin: 0 }}>
            <Message.Header>Alcance elegido</Message.Header>
            <p style={{ margin: '6px 0 0' }}>{templateScopeLabel}</p>
            <p style={{ margin: '6px 0 0' }}>
              {templateSummaryLoading
                ? 'Calculando la descarga…'
                : templateSkuCount === null
                  ? 'La cantidad se confirmará al descargar.'
                  : `La descarga incluirá ${templateSkuCount} SKU.`}
            </p>
          </Message>
          {templateSummaryError ? <Message warning content={templateSummaryError} style={{ margin: 0 }} /> : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            color="blue"
            type="button"
            onClick={handleDownloadTemplate}
            loading={downloadLoading}
            disabled={downloadLoading}
            style={{ minHeight: 48 }}
          >
            <Icon name="download" />
            Descargar plantilla
          </Button>
          <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </div>
        {templateMessage ? <Message positive content={templateMessage} style={{ margin: 0 }} /> : null}
        {fileName ? (
          <Message positive content={`Archivo listo: ${fileName}`} style={{ margin: 0 }} />
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="purple"
            type="button"
            onClick={handlePreview}
            loading={previewLoading}
            disabled={!fileContent || previewLoading}
            style={{ minHeight: 48 }}
          >
            Continuar con validacion
          </Button>
        </div>
      </div>
    </Segment>
  );

  const renderValidationActions = (placement) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: placement === 'bottom' ? 'auto' : 0,
      }}
    >
      <div style={{ color: '#667085', fontSize: 13 }}>
        Se ejecutan {previewSummary?.validRows || 0} de {previewSummary?.totalRows || 0} filas.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button basic type="button" onClick={() => setActiveStep(STEP_TEMPLATE)}>
          Volver a la planilla
        </Button>
        <Button
          color="green"
          type="button"
          onClick={handleExecute}
          loading={executionLoading}
          disabled={executeDisabled}
        >
          Ejecutar importacion
        </Button>
      </div>
    </div>
  );

  const handleStockEdit = async (row, nextStock) => {
    if (!previewData?.draftId || !authUser) return;
    setError('');
    try {
      const response = await stockyApi.updateStockImportStocks(
        previewData.draftId,
        [{ rowNumber: row.rowNumber, nextStock }],
        authUser,
      );
      setPreviewData((current) => ({ ...current, ...response.data }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo actualizar el stock.');
    }
  };

  const validationColumns = [
    { key: 'rowNumber', label: 'Fila', render: (row) => row.rowNumber },
    { key: 'sku', label: 'SKU', render: (row) => <div style={{ fontWeight: 700 }}>{row.sku || '-'}</div> },
    {
      key: 'item',
      label: 'Producto',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.itemTitle || '-'}</div>
          {row.errors?.length > 0 ? (
            <div style={{ color: '#a33333', marginTop: 4 }}>{row.errors.join(' - ')}</div>
          ) : null}
        </>
      ),
    },
    { key: 'currentStock', label: 'Stock actual', align: 'right', render: (row) => formatStock(row.currentStock) },
    {
      key: 'nextStock',
      label: 'Stock nuevo',
      align: 'right',
      width: '1%',
      render: (row) => (
        row.itemId && row.executionPath ? (
          <EditableNumberInput
            value={row.nextStock}
            format={formatStock}
            parse={parseStockInput}
            width={110}
            placeholder="Nuevo stock"
            ariaLabel={`Nuevo stock fila ${row.rowNumber}`}
            onCommit={(nextStock) => handleStockEdit(row, nextStock)}
          />
        ) : '-'
      ),
    },
    {
      key: 'delta',
      label: 'Dif.',
      align: 'right',
      render: (row) => (
        <span style={{ color: resolveDeltaColor(row.quantityDelta), fontWeight: 700 }}>
          {row.nextStock === null ? '-' : formatDelta(row.quantityDelta)}
        </span>
      ),
    },
    {
      key: 'impact',
      label: 'Impacto',
      render: (row) => (
        <>
          <Label basic color={row.executionPath === 'LISTING_INLINE' ? 'blue' : undefined}>
            {resolveImpactLabel(row)}
          </Label>
          {Array.isArray(row.impactedListings) && row.impactedListings.length > 0 ? (
            <div style={{ color: '#667085', marginTop: 6, display: 'grid', gap: 2, fontSize: 12 }}>
              {row.impactedListings.slice(0, 3).map((entry) => (
                <div key={`${row.rowNumber}-${entry.listingId}`}>
                  {entry.marketplaceId} - {entry.listingTitle || 'Publicacion'}
                  {entry.stockEditable === false ? ' (solo lectura)' : ''}
                </div>
              ))}
              {row.impactedListings.length > 3 ? <div>y {row.impactedListings.length - 3} mas</div> : null}
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => (
        <Label color={resolveRowStatusColor(row.status, row.errors)}>
          {resolveRowStatusText(row.status, row.errors)}
        </Label>
      ),
    },
  ];

  const renderValidationStep = () => (
    <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Label color="blue">Filas: {previewSummary?.totalRows || 0}</Label>
        <Label color="green">Validas: {previewSummary?.validRows || 0}</Label>
        <Label color="orange">Omitidas: {previewSummary?.skippedRows || 0}</Label>
        <Label color="teal">Stocks: {previewSummary?.stockUpdates || 0}</Label>
        <Label color="teal">Con fan-out: {previewSummary?.listingFanOutRows || 0}</Label>
        <Label>Solo interno: {previewSummary?.itemOnlyRows || 0}</Label>
      </div>
      {previewSummary?.skippedRows > 0 ? (
        <Message warning content="Las filas invalidas, duplicadas o sin cambios se omitiran automaticamente. Solo se importara lo correcto." />
      ) : null}
      {!previewHasValidRows ? <Message info content="No hay cambios validos para ejecutar." /> : null}
      {renderValidationActions('top')}
      <ImportPreviewTable
        rows={orderedPreviewRows}
        getRowId={(row) => `${row.rowNumber}-${row.itemId || row.sku}`}
        getRowStyle={resolveValidationRowStyle}
        columns={validationColumns}
      />
      {renderValidationActions('bottom')}
    </Segment>
  );

  const resultColumns = [
    { key: 'rowNumber', label: 'Fila', render: (row) => row.rowNumber },
    { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
    {
      key: 'item',
      label: 'Item',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.itemTitle || '-'}</div>
          {row.errors?.length > 0 ? (
            <div style={{ color: '#a33333', marginTop: 4 }}>{row.errors.join(' - ')}</div>
          ) : null}
        </>
      ),
    },
    {
      key: 'stock',
      label: 'Stock',
      render: (row) => (
        <>
          <Label color={row.stockStatus === 'FAILED' ? 'red' : row.stockStatus === 'UPDATED' ? 'green' : undefined}>
            {row.stockStatus}
          </Label>
          <div style={{ marginTop: 4 }}>
            {formatStock(row.stockBefore)} {'->'} {row.stockAfter === null ? '-' : formatStock(row.stockAfter)}
          </div>
        </>
      ),
    },
    {
      key: 'fanOut',
      label: 'Fan-out',
      render: (row) => (
        <>
          <div>OK: {(row.fanOutUpdatedCount || 0) + (row.fanOutInternalOnlyCount || 0)}</div>
          <div>Fallidos: {row.fanOutFailedCount || 0}</div>
        </>
      ),
    },
  ];

  const renderExecutionStep = () => {
    const isRunning = importRunning;
    const hasFinished = importProgress?.status === 'COMPLETED' || importProgress?.status === 'FAILED';
    const hasResults = Array.isArray(importProgress?.results) && importProgress.results.length > 0;

    return (
      <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {isRunning ? 'Procesando importacion' : 'Resultado final'}
          </div>
          <div style={{ color: '#666' }}>
            {isRunning
              ? 'El backend esta aplicando los cambios y sincronizando publicaciones vinculadas.'
              : 'Revisa el detalle fila por fila del procesamiento.'}
          </div>
        </div>
        {isRunning ? (
          <Message warning icon>
            <Icon name="warning sign" />
            <Message.Content>
              <Message.Header>No cierres esta pantalla</Message.Header>
              Espera a que finalice la importacion antes de cerrar el wizard, recargar o navegar a otra pantalla.
            </Message.Content>
          </Message>
        ) : null}
        <Progress
          percent={progressPercent}
          indicating={isRunning}
          success={importProgress?.status === 'COMPLETED'}
          error={importProgress?.status === 'FAILED'}
          style={{ marginBottom: 0 }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Label color="blue">Procesadas: {importProgress?.processedRows || 0}/{importProgress?.totalRows || 0}</Label>
          <Label color="green">SKUs impactados: {importProgress?.stockUpdatedItems || 0}</Label>
          <Label color="green">Fan-out OK: {importProgress?.fanOutUpdatedTargets || 0}</Label>
          <Label color={(importProgress?.fanOutFailedTargets || 0) > 0 ? 'red' : undefined}>Fan-out fallido: {importProgress?.fanOutFailedTargets || 0}</Label>
          <Label color={(importProgress?.failedRows || 0) > 0 ? 'red' : undefined}>Filas con error: {importProgress?.failedRows || 0}</Label>
        </div>
        {executionLoading && (!importProgress || importProgress.processedRows === 0) ? (
          <Message info icon>
            <Icon name="spinner" loading />
            <Message.Content>
              <Message.Header>Iniciando importacion</Message.Header>
              En cuanto el backend confirme el primer cambio, veras avanzar el contador.
            </Message.Content>
          </Message>
        ) : null}
        {importProgress?.error ? <Message negative content={importProgress.error} /> : null}
        {hasFinished && !hasResults ? (
          <Message warning content="La importacion finalizo pero no devolvio filas procesadas." />
        ) : null}
        <ImportPreviewTable
          rows={importProgress?.results || []}
          getRowId={(row) => `${row.rowNumber}-${row.itemId || row.sku}`}
          columns={resultColumns}
        />
      </Segment>
    );
  };

  const renderCurrentStep = () => {
    if (activeStep === STEP_TEMPLATE) return renderTemplateStep();
    if (activeStep === STEP_VALIDATION) return renderValidationStep();
    return renderExecutionStep();
  };

  const handleClose = () => {
    if (importRunning) {
      return;
    }
    onClose();
  };

  return (
    <ImportWizardModal
      open={open}
      title="Importar stock"
      onClose={handleClose}
      closeLocked={importRunning}
      stepHeader={renderStepHeader()}
      error={error}
    >
      {renderCurrentStep()}
    </ImportWizardModal>
  );
};

export default BulkStockImportWizardModal;
